import React, { useState } from "react";
import { getCookie, logAction } from "./log.js";
import "../css/PopUpAddFile.css";

const PopUpAddFile = ({ object, onClose }) => {
    /**
     * selectedFile : fichier sélectionné par l'utilisateur via l'input file.
     * Initialisé à null, mis à jour au changement d'input.
     */
    const [selectedFile, setSelectedFile] = useState(null);

    /**
     * previewUrl : URL temporaire générée localement pour afficher un aperçu
     * du fichier sélectionné (image ou PDF). Null si pas de fichier sélectionné.
     */
    const [previewUrl, setPreviewUrl] = useState(null);

    /**
     * API_URL : URL de base de l'API, récupérée depuis les variables
     * d'environnement via process.env.REACT_APP_API_URL.
     * Utilisée pour construire les requêtes fetch vers le serveur.
     */
    const API_URL = process.env.REACT_APP_API_URL;

    /**
     * handleFileChange : gestionnaire d'événement au changement de fichier.
     * Récupère le premier fichier sélectionné, le stocke dans selectedFile,
     * et génère une URL locale pour l'aperçu dans previewUrl.
     * @param {Event} e - Événement de changement d'input.
     */
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    /**
     * handleSubmit : gestionnaire de soumission du formulaire.
     * Empêche le comportement par défaut, vérifie qu'un fichier est sélectionné,
     * appelle handleNewFile pour uploader le fichier, puis ferme la popup.
     * @param {Event} e - Événement de soumission du formulaire.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            alert("Veuillez sélectionner un fichier.");
            return;
        }
        await handleNewFile();
        onClose();
    };

    /**
     * handleNewFile : fonction asynchrone qui prépare et envoie le fichier au serveur.
     * Utilise FormData pour l'envoi multipart/form-data.
     * Logue l'action d'ajout en cas de succès.
     */
    const handleNewFile = async () => {
        const userId = getCookie("userId");

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("dossier", object.id);
        formData.append("sousdossier", object.pathArray.join("\\"));
        formData.append("type", object.type);
        formData.append("nom", selectedFile.name);

        try {
            const response = await fetch(`${API_URL}/api/addNewObject.php`, {
                method: "POST",
                credentials: 'include',
                headers: { "Accept": "application/json" }, // Pas de Content-Type, fetch gère pour FormData
                body: formData,
            });

            const result = await response.json();

            if (response.ok) {
                logAction({
                    action: "addFile",
                    dossierId: object.id,
                    utilisateurId: userId,
                    fichier: selectedFile.name,
                    path: object.pathArray.join("\\"),
                });
                console.log("upload ok");
                
            } else {
                alert(`Erreur: ${result.error}`);
            }
        } catch (err) {
            console.error("Erreur lors de l'upload du fichier :", err);
            alert("Une erreur est survenue.");
        }
    };

    /**
     * renderPreview : fonction de rendu conditionnel pour l'aperçu du fichier.
     * Supporte images et PDF, sinon message d'absence d'aperçu.
     * @returns JSX ou null
     */
    const renderPreview = () => {
        if (!previewUrl) return null;

        if (selectedFile.type.startsWith("image/")) {
            return <img src={previewUrl} alt="Aperçu" className="file-preview" />;
        }

        if (selectedFile.type === "application/pdf") {
            return (
                <iframe
                    src={previewUrl}
                    title="Aperçu PDF"
                    className="file-preview"
                />
            );
        }

        return <p>Aperçu non disponible pour ce type de fichier.</p>;
    };

    /**
     * formatFileSize : formate la taille du fichier en Ko ou Mo selon la grandeur.
     * @param {number} bytes - taille du fichier en octets
     * @returns {string} taille formatée en "XX.XX Mo" ou "XX.X Ko"
     */
    const formatFileSize = (bytes) => {
        const kb = bytes / 1024;
        return kb > 1024 ? `${(kb / 1024).toFixed(2)} Mo` : `${kb.toFixed(1)} Ko`;
    };

    return (
        <div className="popupAdFile-overlay">
            <div className="popupAdFile-content">
                <div className="popupAdFile-form">
                    <h3>Ajouter un fichier</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            {/* <label>Sélectionner un fichier :</label> */}
                            <input className="file-input " type="file" onChange={handleFileChange} />
                        </div>

                        {selectedFile && (
                            <div className="file-info">
                                <p><strong>Nom :</strong> {selectedFile.name}</p>
                                <p><strong>Taille :</strong> {formatFileSize(selectedFile.size)}</p>
                                <p><strong>Type :</strong> {selectedFile.type}</p>
                            </div>
                        )}

                        <div className="flex justify-end -popupAdFile-buttons">
                            <button className="btn btn-success mr-2" type="submit">Ajouter</button>
                            <button className="btn" type="button" onClick={onClose}>Annuler</button>
                        </div>
                    </form>
                </div>

                {previewUrl && (
                    <div className="preview-container">
                        {renderPreview()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PopUpAddFile;
